import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { MSpinner } from "components/Shared";
import { getStatus, postStart } from "requests/PhoenixIntegration";
import responseHandler from "responseHandler";
import { rxEmailAddress, rxPhoneNumber } from "tools/regularExpressions";
import { EMAIL, TEXT } from "typeConstants/stringConstants";

const STATUS = {
  // ui states
  NONE: "NONE",
  FETCHING: "FETCHING",
  ERROR: "ERROR",
  SUBMITTED: "SUBMITTED",

  // response states
  RESPONSE_ERROR: "RESPONSE_ERROR",
  READY: "READY",
  INVALID: "INVALID",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "COMPLETED",
};

const PhoenixAtHome = ({ match: { params }, makeToast }) => {
  const mounted = useRef(true);

  const [email, setEmail] = useState();
  const [phone, setPhoneNumber] = useState();
  const [status, setStatus] = useState(STATUS.NONE);
  const [errorMessage, setErrorMessage] = useState("Error");
  const [warningVisible, setVisibile] = useState(true);
  const [typeInspectionModal, setInspectionModal] = useState("");
  const [inspectionModalVisible, setInspectionModalVisible] = useState(false);

  const fetchStatus = () => {
    setStatus(STATUS.FETCHING);

    getStatus(params.fileid, 
      (resp) => {
        setStatus(resp.Status);
      },
      () =>{
        setStatus(STATUS.ERROR);
        makeToast("Error");
      }, 
      mounted);        
  };

  useEffect(() => {
    if(status === STATUS.NONE)
      fetchStatus();

    return () => { mounted.current = false; };
  }, []);

  const submitRequest = () => {
    if (email && !rxEmailAddress.test(email)) {
      makeToast("Invalid Email Address.");
      return;
    } 
    if (phone && !rxPhoneNumber.test(phone)) {
      makeToast("Invalid Phone Number.");
      return;
    }

    setStatus(STATUS.FETCHING);

    postStart(params.fileid, email, phone,
      (resp) => {
        if(!resp.error) {
          setStatus(STATUS.SUBMITTED);
        }
        else {
          setErrorMessage(resp.message);
          setStatus(STATUS.RESPONSE_ERROR);
        }
      },
      () => {
        setStatus(STATUS.ERROR);
        makeToast("Error");
      }, 
      mounted); 
   
  };

  const hideDialogWarning = () => {
    setVisibile(false);
  };

  const createInspectionEmail = () => {
    setInspectionModal(EMAIL);
    setInspectionModalVisible(true);
  };

  const createInspectionText = () => {
    setInspectionModal(TEXT);
    setInspectionModalVisible(true);
  };

  return (
    <div className="policyholder-inspection-root">
      {(status === STATUS.NONE || status === STATUS.FETCHING) && <MSpinner />}
      {status === STATUS.ERROR && <h1>Request Error, please try again.</h1>}
      {status === STATUS.INVALID && <h1>This case is not compatible with the Mueller@Home Inspection workflow.</h1>}      
      {status === STATUS.SUBMITTED && <h1>Mueller@Home Inspection Successfully Created.</h1>}
      {status === STATUS.COMPLETED && <h1>This inspection already has a completed Mueller@Home Inspection.  Submitting this form will create a new one.</h1>}
      {status === STATUS.RESPONSE_ERROR && <h1>Error: {errorMessage}</h1>}
      {status === STATUS.IN_PROGRESS && 
        <Dialog 
        open={warningVisible} 
        onClose={() => setVisibile(false)} 
        id={STATUS.IN_PROGRESS} 
        className="inspection-warning"
        sx={{ '& .MuiDialog-paper': { width: 500 } }}>
          <DialogTitle>{"!!! WARNING !!!"}</DialogTitle>
          <DialogContent>
            <h6 className="warning-content">This inspection has an in-progress Mueller@Home Inspection.  Submitting this form will cancel the open inspection and create a new one.</h6>
          </DialogContent>
          <DialogActions>
            <Button onClick={hideDialogWarning}>Proceed with next inspection</Button>
          </DialogActions>
        </Dialog>}
      {(status === STATUS.READY || status === STATUS.IN_PROGRESS || status === STATUS.COMPLETED) &&
      <>
        <h3>Create a Mueller@Home Inspection</h3>
        <Button onClick={createInspectionEmail}>Email</Button>
        <Button onClick={createInspectionText}>Text Message</Button>
        {typeInspectionModal === EMAIL ? 
          (<Dialog 
            aria-label="Create a Mueller@Home Inspection Email"
            id="ph-email" 
            open={inspectionModalVisible}
            sx={{ '& .MuiDialog-paper': { width: 500 } }}>
            <DialogTitle>Create a Mueller@Home Inspection Email</DialogTitle>
            <DialogContent>
              <TextField
                id="ph-email-address"
                variant="standard"
                label="Enter Email:"
                inputProps={{ maxLength: 100 }}
                type="email"
                onChange={(e) => setEmail(e.target.value)}
                sx={{ minWidth: 240, maxWidth: 240 }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={submitRequest}>Submit</Button>
              <Button variant="text" color="secondary" onClick={() => {
                setInspectionModalVisible(false);
                setEmail();
                }}>Cancel</Button>
            </DialogActions>
          </Dialog>) :
         ( <Dialog 
          aria-label="Create a Mueller@Home Inspection Text"
          id="ph-text" 
          open={inspectionModalVisible}
          sx={{ '& .MuiDialog-paper': { width: 500 } }}>
          <DialogTitle>Create a Mueller@Home Inspection Text</DialogTitle>
          <DialogContent>
            <TextField
              id="ph-phone-number"
              variant="standard"
              label="Enter 10 Digit Phone Number:"
              inputProps={{ maxLength: 10 }}
              type="text"
              onChange={(e) => setPhoneNumber(e.target.value)}
              sx={{ minWidth: 240, maxWidth: 240 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={submitRequest}>Submit</Button>
            <Button variant="text" color="secondary" onClick={() => {
              setInspectionModalVisible(false);
              setPhoneNumber();
              }}>Cancel</Button>
          </DialogActions>
        </Dialog>)
        }
       
      </>
      }      
    </div>
  );
};

PhoenixAtHome.propTypes = {
  match: PropTypes.object.isRequired,
  makeToast: PropTypes.func.isRequired,
};

const mapDispatchToProps = dispatch => ({
  makeToast: text => {
    responseHandler.customToast(text, "error", dispatch);
  }
});

export default connect(null, mapDispatchToProps)(PhoenixAtHome);